import { useState } from "react";
import { Dot, DisplayText, BodyText, alpha, borderAlpha } from "../../components/mobile/primitives";
import { JourneyProgress, OnboardingFooter, DataNeedIcon } from "./journey-shared";

export function JourneyPickSkillScreen() {
  const [expanded, setExpanded] = useState("Sleep Deep Dive");

  const skills = [
    {
      accent: "var(--color-kin-lavender)",
      title: "Sleep Deep Dive",
      author: "Dr. Sara Chen",
      tagline: "Find out why some nights recover and others don't.",
      produces: ["Sleep score + trend", "Recovery patterns", "3 recommendations"],
      needs: [
        { type: "wearable", label: "Wearable" },
        { type: "sleep", label: "Sleep logs" },
      ],
      selected: true,
    },
    {
      accent: "var(--color-kin-green-soft)",
      title: "Lab Panel Review",
      author: "Jonas Weber",
      tagline: "Know which markers to watch and which to ignore.",
      produces: ["Flagged markers", "Trends over time", "Follow-up suggestions"],
      needs: [{ type: "lab", label: "Lab report" }],
      selected: false,
    },
    {
      accent: "var(--color-kin-gold)",
      title: "Nutrition Audit",
      author: "Jonas Weber",
      tagline: "Spot the dietary gaps your tracker misses.",
      produces: ["Macro balance", "Nutrient gaps", "Meal timing insights"],
      needs: [{ type: "meals", label: "7+ days meal logs" }],
      selected: false,
    },
    {
      accent: "var(--color-kin-rose)",
      title: "Cardio Fitness",
      author: "Dr. Sara Chen",
      tagline: "See if your training is actually improving your heart.",
      produces: ["VO2 trend estimate", "Intensity split", "Recovery readiness"],
      needs: [
        { type: "wearable", label: "Wearable" },
        { type: "workout", label: "Workouts" },
      ],
      selected: false,
    },
  ];

  return (
    <div
      style={{
        padding: "0 16px 0",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        height: "100%",
        position: "relative",
      }}
    >
      {/* Hero band */}
      <div
        style={{
          margin: "0 -16px",
          padding: "14px 16px 14px",
          background: `linear-gradient(135deg, ${alpha("var(--color-kin-green-soft)", "14")}, ${alpha("var(--color-kin-green-soft)", "06")})`,
          borderBottom: borderAlpha("var(--color-kin-green-soft)", "20"),
        }}
      >
        <JourneyProgress current={1} />
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, marginTop: 8 }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "var(--color-kin-green-soft)",
            }}
          />
          <BodyText
            size={8}
            color="var(--color-kin-green-soft)"
            weight={700}
            style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}
          >
            Step 1 of 4
          </BodyText>
        </div>
        <DisplayText size={20}>Choose your first skill</DisplayText>
        <BodyText
          size={9}
          color="var(--color-kin-muted)"
          weight={500}
          style={{ marginTop: 3, lineHeight: 1.45 }}
        >
          You can add more later, create your own, or skip.
        </BodyText>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        {skills.map((skill) => {
          const isExpanded = expanded === skill.title;
          return (
            <div
              key={skill.title}
              style={{
                padding: "9px 11px",
                borderRadius: 14,
                cursor: "pointer",
                background: skill.selected
                  ? `linear-gradient(135deg, ${alpha(skill.accent, "10")}, ${alpha(skill.accent, "05")})`
                  : "rgba(254,253,250,0.50)",
                border: skill.selected
                  ? borderAlpha(skill.accent, "40")
                  : "1px solid rgba(0,0,0,0.04)",
                backdropFilter: "blur(12px)",
                transition: "all 0.2s ease",
              }}
              onClick={() => setExpanded(isExpanded ? null : skill.title)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                {/* Accent dot */}
                <Dot color={skill.accent} size={9} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Title + author */}
                  <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                    <BodyText size={11} weight={700} color="var(--color-kin-black)">
                      {skill.title}
                    </BodyText>
                    <BodyText size={8.5} color="var(--color-kin-muted)" weight={500}>
                      {skill.author}
                    </BodyText>
                  </div>
                  {/* Needs — icons with labels */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                    {skill.needs.map((need) => (
                      <div
                        key={need.type}
                        style={{ display: "flex", alignItems: "center", gap: 3 }}
                      >
                        <DataNeedIcon type={need.type} accent={skill.accent} />
                        <BodyText size={8} color="var(--color-kin-muted)" weight={600}>
                          {need.label}
                        </BodyText>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Selection indicator */}
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: skill.selected ? skill.accent : "transparent",
                    border: skill.selected ? "none" : "1.5px solid rgba(0,0,0,0.10)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {skill.selected && (
                    <svg width="10" height="10" viewBox="0 0 12 12">
                      <path
                        d="M2.5 6 L5 8.5 L9.5 3.5"
                        stroke="#fff"
                        strokeWidth="1.8"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Tagline + expanded output preview */}
              <div style={{ marginTop: 6, paddingLeft: 19 }}>
                <BodyText
                  size={9}
                  color="var(--color-kin-muted)"
                  weight={500}
                  style={{ lineHeight: 1.45 }}
                >
                  {skill.tagline}
                </BodyText>
                {isExpanded ? (
                  <div style={{ marginTop: 8 }}>
                    <BodyText
                      size={7.5}
                      color="#b0a89f"
                      weight={700}
                      style={{
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        marginBottom: 5,
                      }}
                    >
                      You'll get
                    </BodyText>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {skill.produces.map((item) => (
                        <div
                          key={item}
                          style={{
                            padding: "3px 8px",
                            borderRadius: 8,
                            background: alpha(skill.accent, "10"),
                            border: borderAlpha(skill.accent, "20"),
                          }}
                        >
                          <BodyText size={8} color={skill.accent} weight={700}>
                            {item}
                          </BodyText>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <BodyText
                    size={8.5}
                    color={skill.accent}
                    weight={600}
                    style={{ cursor: "pointer", marginTop: 4 }}
                  >
                    What you'll get ›
                  </BodyText>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Continue — pinned to bottom */}
      <OnboardingFooter label="Continue" enabled={true} showSkip={true} />
    </div>
  );
}
